import React, { useState } from 'react';
import { contactInfo, socialLinks } from '../data/mock';
import { MapPin, Phone, Mail, Send, Facebook, Twitter, Linkedin, Instagram, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';

// Use relative path for Vercel, fallback to env variable for local dev
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const socialIcons = {
  facebook: Facebook,
  twitter: Twitter,
  linkedin: Linkedin,
  instagram: Instagram
};

const ContactSection = () => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: '',
    honeypot: '' // Honeypot field for spam protection
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear status when user starts typing again
    if (submitStatus) setSubmitStatus(null);
  };

  const validateForm = () => {
    const errors = [];
    
    // Name validation
    if (formData.name.trim().length < 2) {
      errors.push('Name must be at least 2 characters');
    }
    if (!/^[a-zA-Z\s\-'\.]+$/.test(formData.name)) {
      errors.push('Name contains invalid characters');
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      errors.push('Please enter a valid email address');
    }
    
    // Message validation
    if (formData.message.trim().length < 10) {
      errors.push('Message must be at least 10 characters');
    }
    if (formData.message.length > 5000) {
      errors.push('Message is too long (max 5000 characters)');
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    const errors = validateForm();
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Use /api/contact endpoint (works on both local and Vercel)
      const response = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          message: formData.message.trim(),
          service: formData.service || 'General Enquiry',
          honeypot: formData.honeypot // Should always be empty
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', service: '', message: '', honeypot: '' });
      } else if (response.status === 429) {
        setSubmitStatus('error');
        toast.error('Too many submissions. Please try again later.', {
          icon: <AlertCircle className="w-5 h-5 text-red-500" />
        });
      } else {
        setSubmitStatus('error');
        toast.error(data.detail || data.message || 'Failed to send message. Please try again.', {
          icon: <AlertCircle className="w-5 h-5 text-red-500" />
        });
      }
    } catch (error) {
      setSubmitStatus('error');
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please check your connection and try again.', {
        icon: <AlertCircle className="w-5 h-5 text-red-500" />
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = `w-full border rounded-lg sm:rounded-xl px-4 sm:px-5 py-3 sm:py-4 text-sm sm:text-base focus:outline-none focus:ring-2 transition-all duration-300 ${
    isDark 
      ? 'bg-[#1A2420] border-green-900/30 text-white focus:ring-green-500/30 focus:border-green-700 placeholder:text-gray-500' 
      : 'bg-white border-gray-200 text-gray-900 focus:ring-green-500/20 focus:border-green-500 placeholder:text-gray-400'
  }`;

  return (
    <section id="contact" className={`py-16 sm:py-24 lg:py-32 ${isDark ? 'bg-[#0A0F0D]' : 'bg-[#FAFAFA]'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Section header */}
        <div className="mb-10 sm:mb-16">
          <p className={`font-medium text-xs sm:text-sm uppercase tracking-[0.2em] mb-3 sm:mb-4 ${
            isDark ? 'text-green-500' : 'text-green-600'
          }`}>
            Get In Touch
          </p>
          <h2 className="font-display font-bold leading-tight mb-4 sm:mb-6" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)' }}>
            <span className={isDark ? 'text-white' : 'text-gray-900'}>Contact </span>
            <span className="gradient-text">Us</span>
          </h2>
          <p className={`text-sm sm:text-base lg:text-lg max-w-2xl ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Have a question? Need an enquiry on any of our services? We're here to help.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
          {/* Contact Form */}
          <div className={`rounded-xl sm:rounded-2xl p-6 sm:p-8 lg:p-10 border ${
            isDark 
              ? 'bg-[#111916] border-green-900/20' 
              : 'bg-white border-gray-100 shadow-xl shadow-gray-100/50'
          }`}>
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {/* Honeypot field - hidden from users, catches bots */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleChange}
                style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              
              <div>
                <label className={`block font-medium text-xs sm:text-sm mb-2 sm:mb-3 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  minLength={2}
                  maxLength={100}
                  className={inputClasses}
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label className={`block font-medium text-xs sm:text-sm mb-2 sm:mb-3 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={inputClasses}
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className={`block font-medium text-xs sm:text-sm mb-2 sm:mb-3 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Service Interest
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className={`${inputClasses} appearance-none cursor-pointer`}
                >
                  <option value="">Select a service...</option>
                  <option value="renewable-energy">Renewable Energy</option>
                  <option value="agro-culture">Agro-Culture</option>
                  <option value="engineering">Engineering Solutions</option>
                  <option value="general">General Enquiry</option>
                </select>
              </div>

              <div>
                <label className={`block font-medium text-xs sm:text-sm mb-2 sm:mb-3 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  minLength={10}
                  maxLength={5000}
                  rows={4}
                  className={`${inputClasses} resize-none`}
                  placeholder="Tell us about your needs..."
                />
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {formData.message.length}/5000 characters
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full flex items-center justify-center gap-2 sm:gap-3 bg-gradient-to-r from-[#006633] to-[#00873D] text-white font-semibold text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4 rounded-full hover:shadow-xl hover:shadow-green-500/25 hover:scale-[1.01] active:scale-[0.99] transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100 ${
                  isSubmitting ? 'animate-pulse' : ''
                }`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>
              
              {/* Success Message - appears below button */}
              {submitStatus === 'success' && (
                <div className={`flex items-center justify-center gap-2 p-4 rounded-xl text-center ${
                  isDark 
                    ? 'bg-green-900/20 border border-green-700/30' 
                    : 'bg-green-50 border border-green-200'
                }`}>
                  <CheckCircle className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <div>
                    <p className={`font-medium ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                      Message sent successfully!
                    </p>
                    <p className={`text-sm ${isDark ? 'text-green-500/80' : 'text-green-600'}`}>
                      We'll get back to you within 24-48 hours.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Error Message - appears below button */}
              {submitStatus === 'error' && (
                <div className={`flex items-center justify-center gap-2 p-4 rounded-xl text-center ${
                  isDark 
                    ? 'bg-red-900/20 border border-red-700/30' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <AlertCircle className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                  <p className={`font-medium ${isDark ? 'text-red-400' : 'text-red-700'}`}>
                    Failed to send. Please try again.
                  </p>
                </div>
              )}
              
              <p className={`text-xs text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Your information is secure and will never be shared.
              </p>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            {/* Address */}
            <div className={`flex items-start gap-4 sm:gap-5 p-4 sm:p-6 rounded-xl sm:rounded-2xl border transition-all duration-300 ${
              isDark 
                ? 'bg-[#111916] border-green-900/20 hover:border-green-800/40' 
                : 'bg-white border-gray-100 hover:border-green-200 shadow-sm'
            }`}>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-[#006633] to-[#00873D] flex items-center justify-center flex-shrink-0 shadow-lg shadow-green-500/20">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div>
                <h3 className={`font-semibold text-base sm:text-lg mb-1 sm:mb-2 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  Office Address
                </h3>
                <p className={`text-sm sm:text-base leading-relaxed ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {contactInfo.address}
                </p>
              </div>
            </div>

            {/* Phone */}
            <div className={`flex items-start gap-4 sm:gap-5 p-4 sm:p-6 rounded-xl sm:rounded-2xl border transition-all duration-300 ${
              isDark 
                ? 'bg-[#111916] border-green-900/20 hover:border-green-800/40' 
                : 'bg-white border-gray-100 hover:border-green-200 shadow-sm'
            }`}>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-[#00873D] to-[#006633] flex items-center justify-center flex-shrink-0 shadow-lg shadow-green-500/20">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div>
                <h3 className={`font-semibold text-base sm:text-lg mb-1 sm:mb-2 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  Phone
                </h3>
                <div className="space-y-1">
                  {contactInfo.phones.map((phone, index) => (
                    <a 
                      key={index}
                      href={`tel:${phone.replace(/\s/g, '')}`}
                      className={`block text-sm sm:text-base transition-colors ${
                        isDark ? 'text-gray-400 hover:text-green-400' : 'text-gray-600 hover:text-green-600'
                      }`}
                    >
                      {phone}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Email */}
            <div className={`flex items-start gap-4 sm:gap-5 p-4 sm:p-6 rounded-xl sm:rounded-2xl border transition-all duration-300 ${
              isDark 
                ? 'bg-[#111916] border-green-900/20 hover:border-green-800/40' 
                : 'bg-white border-gray-100 hover:border-green-200 shadow-sm'
            }`}>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-[#FF8C00] to-[#FFD700] flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/20">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div>
                <h3 className={`font-semibold text-base sm:text-lg mb-1 sm:mb-2 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  Email
                </h3>
                <div className="space-y-1">
                  {contactInfo.emails.map((email, index) => (
                    <a 
                      key={index}
                      href={`mailto:${email}`}
                      className={`block text-sm sm:text-base transition-colors break-all ${
                        isDark ? 'text-gray-400 hover:text-green-400' : 'text-gray-600 hover:text-green-600'
                      }`}
                    >
                      {email}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className={`p-4 sm:p-6 rounded-xl sm:rounded-2xl border ${
              isDark 
                ? 'bg-[#111916] border-green-900/20' 
                : 'bg-white border-gray-100 shadow-sm'
            }`}>
              <h3 className={`font-semibold text-base sm:text-lg mb-4 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                Follow Us
              </h3>
              <div className="flex items-center gap-3">
                {socialLinks.map((social) => {
                  const IconComponent = socialIcons[social.icon];
                  return (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isDark 
                          ? 'bg-green-900/30 text-green-400 hover:bg-gradient-to-br hover:from-[#006633] hover:to-[#00873D] hover:text-white hover:scale-110 hover:shadow-lg hover:shadow-green-500/20' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gradient-to-br hover:from-[#006633] hover:to-[#00873D] hover:text-white hover:scale-110 hover:shadow-lg hover:shadow-green-500/20'
                      }`}
                      aria-label={social.name}
                    >
                      <IconComponent size={18} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
